import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCar, 
  faSpinner, 
  faCalendarAlt, 
  faPalette,
  faSearch,
  faChevronDown,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import vehicleApi from '../../utils/vehicleApi';
import './VehicleForm.css';

const SearchableDropdown = ({ 
  options = [],
  value, 
  onChange, 
  placeholder, 
  disabled = false,
  loading = false,
  name,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    if (!option) return false;
    
    const optionText = typeof option === 'object' ? option.label : option.toString();
    return optionText && optionText.toLowerCase().includes((searchTerm || '').toLowerCase());
  });
  
  const handleSelect = (option) => {
    const optionValue = typeof option === 'object' ? option.value : option;
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
    setSearchTerm('');
  };
  
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleClear = () => {
    onChange({ target: { name, value: '' } });
    setSearchTerm('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  // Get display value
  const getDisplayValue = () => {
    if (!value || !options || options.length === 0) return '';
    
    const selectedOption = options.find(option => {
      if (!option) return false;
      const optionValue = typeof option === 'object' ? option.value : option;
      return optionValue === value;
    });
    
    if (!selectedOption) return value;
    
    return typeof selectedOption === 'object' ? selectedOption.label : selectedOption.toString();
  };
  
  return (
    <div className={`searchable-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div 
        className={`dropdown-header ${isOpen ? 'active' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {!isOpen ? (
          <>
            <div className="selected-value">
              {value ? getDisplayValue() : placeholder}
            </div>
            {value && (
              <button 
                type="button" 
                className="clear-button" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
            <div className="dropdown-icon">
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} />
              )}
            </div>
          </>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm || ''}
            onChange={handleInputChange}
            placeholder={`Search ${placeholder ? placeholder.toLowerCase() : ''}...`}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
      
      {isOpen && (
        <ul className="dropdown-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              if (!option) return null;
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option.toString();
              
              return (
                <li 
                  key={index} 
                  onClick={() => handleSelect(option)}
                  className={value === optionValue ? 'selected' : ''}
                >
                  {optionLabel}
                </li>
              );
            })
          ) : (
            <li className="no-results">No results found</li>
          )}
        </ul>
      )}
      
      {required && !value && (
        <input 
          type="text" 
          style={{ display: 'none' }} 
          required 
          value={value || ''}
          onChange={() => {}}
        />
      )}
    </div>
  );
};

const VehicleForm = ({ onSubmit, initialData = {} }) => {
  const [formData, setFormData] = useState({
    year: initialData.year || '',
    make: initialData.make || '',
    model: initialData.model || '',
    color: initialData.color || '',
    additional_details: initialData.additional_details || '',
    distance_preference: initialData.distance_preference || 'any',
    ...initialData
  });
  
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [colors, setColors] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState({
    makes: false,
    models: false
  });
  
  // Fetch makes, colors, and years on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(prev => ({ ...prev, makes: true }));
      
      try {
        // Fetch makes (car manufacturers only)
        const makesData = await vehicleApi.getAllMakes(true);
        if (Array.isArray(makesData)) {
          setMakes(makesData.map(make => ({
            value: make.Make_Name,
            label: make.Make_Name
          })));
        }
        
        // Get colors
        const colorsData = vehicleApi.getCommonColors();
        if (Array.isArray(colorsData)) {
          setColors(colorsData.map(color => ({
            value: color,
            label: color
          })));
        }
        
        // Get years
        const yearsData = vehicleApi.getYearRange();
        if (Array.isArray(yearsData)) {
          setYears(yearsData.map(year => ({
            value: year.toString(),
            label: year.toString()
          })));
        }
      } catch (error) {
        console.error('Error fetching vehicle data:', error);
      } finally {
        setLoading(prev => ({ ...prev, makes: false }));
      }
    };
    
    fetchInitialData();
  }, []);
  
  // Fetch models when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!formData.make) {
        setModels([]);
        return;
      }
      
      setLoading(prev => ({ ...prev, models: true }));
      
      try {
        const modelsData = await vehicleApi.getModelsByMake(formData.make);
        if (Array.isArray(modelsData)) {
          setModels(modelsData.map(model => ({
            value: model.Model_Name,
            label: model.Model_Name
          })));
        }
      } catch (error) {
        console.error(`Error fetching models for ${formData.make}:`, error);
      } finally {
        setLoading(prev => ({ ...prev, models: false }));
      }
    };
    
    fetchModels();
  }, [formData.make]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };
  
  return (
    <form className="vehicle-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <FontAwesomeIcon icon={faCar} />
        <h2>Vehicle Information</h2>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="year">
            <FontAwesomeIcon icon={faCalendarAlt} />
            Year
          </label>
          <SearchableDropdown
            name="year"
            options={years}
            value={formData.year}
            onChange={handleChange}
            placeholder="Select Year"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="make">
            <FontAwesomeIcon icon={faCar} />
            Make
          </label>
          <SearchableDropdown
            name="make"
            options={makes}
            value={formData.make}
            onChange={handleChange}
            placeholder="Select Make"
            loading={loading.makes}
            disabled={loading.makes}
            required
          />
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="model">
            <FontAwesomeIcon icon={faSearch} />
            Model
          </label>
          <SearchableDropdown
            name="model"
            options={models}
            value={formData.model}
            onChange={handleChange}
            placeholder="Select Model"
            loading={loading.models}
            disabled={!formData.make || loading.models}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="color">
            <FontAwesomeIcon icon={faPalette} />
            Color
          </label>
          <SearchableDropdown
            name="color"
            options={colors}
            value={formData.color}
            onChange={handleChange}
            placeholder="Select Color (Optional)"
          />
        </div>
      </div>
      
      <div className="form-group">
        <label htmlFor="distance_preference">Distance Preference</label>
        <select
          id="distance_preference"
          name="distance_preference"
          value={formData.distance_preference}
          onChange={handleChange}
          required
        >
          <option value="25">25 Miles or Less</option>
          <option value="50">50 Miles or Less</option>
          <option value="100">100 Miles or Less</option>
          <option value="250">250 Miles or Less</option>
          <option value="any">Any Distance</option>
        </select>
      </div>
      
      <div className="form-group">
        <label htmlFor="additional_details">Additional Details</label>
        <textarea
          id="additional_details"
          name="additional_details"
          value={formData.additional_details || ''}
          onChange={handleChange}
          placeholder="Enter any additional details about the vehicle you're looking for..."
          rows={4}
        />
      </div>
      
      <button type="submit" className="submit-button">
        Submit
      </button>
    </form>
  );
};

export default VehicleForm; 